import React, { useEffect, useMemo, useRef, useState } from 'react'
import block from 'bem-cn'

import EmptyTableIcon from 'shared/img/empty-table.webp'
import { ReactComponent as EmptyTransactionsIcon } from 'shared/img/empty-transactions.svg'

import { useAppTranslation } from 'shared/hooks/useAppTranslation'

import { Skeleton } from 'components/UIKit/Skeleton'

import { Pagination } from '../Pagination'
import { UsePaginationReturn } from './hooks'
import { Row, RowVariants } from './Row'
import { ColumnType, DefaultDataType } from './types'
import { formatWidth } from './utils'

import './Table.scss'

type TableProps<D> = {
  columns: ColumnType<D>[]
  data?: DefaultDataType<D>[]
  pagination?: UsePaginationReturn
  onRowClick?: (item: DefaultDataType<D>) => void
  emptyTitle?: string
  emptyText?: string
  rowKey?: (item: DefaultDataType<D>) => string
  isLoading?: boolean
  isTransactions?: boolean
}

const b = block('table')

export const Table = <D,>({
  columns,
  pagination,
  onRowClick,
  data,
  isLoading,
  emptyText,
  rowKey,
  emptyTitle,
  isTransactions,
}: TableProps<D>) => {
  const ref = useRef<HTMLTableElement>(null)
  const [hasScroll, setHasScroll] = useState(false)
  const isEmpty = !data?.length && !isLoading
  const { tPrefix } = useAppTranslation('common')

  const gridTemplateColumns = useMemo(
    () =>
      columns
        .map(column => {
          const formattedWidth = formatWidth(column.width)
          const formattedMinWidth = formatWidth(column.minWidth)
          const formattedMaxWidth = formatWidth(column.maxWidth)

          if (!formattedWidth && !formattedMinWidth && !formattedMaxWidth) {
            return '1fr'
          }

          return `minmax(${formattedMinWidth || formattedWidth}, ${formattedMaxWidth || formattedWidth || '1fr'})`
        })
        .join(' '),
    [columns],
  )

  const rows = useMemo(
    () =>
      data?.map(item => (
        <Row<D>
          key={rowKey?.(item) || item.id}
          onRowClick={onRowClick}
          columns={columns}
          item={item}
          hasScroll={hasScroll}
        />
      )),
    [data, columns, onRowClick, hasScroll, rowKey],
  )

  const renderedHeader = useMemo(
    () => <Row<D> variant={RowVariants.HEAD_ROW} columns={columns} hasScroll={hasScroll} className={b('header-row')} />,
    [columns, hasScroll],
  )

  useEffect(() => {
    const checkScroll = () => {
      if (ref.current) {
        setHasScroll(ref.current.scrollWidth > ref.current.clientWidth)
      }
    }

    const observer = new ResizeObserver(checkScroll)

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer?.disconnect()
  }, [])

  return (
    <div className={b('container')}>
      {/* {isLoading && (
        <div className={b('loading')}>
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      )} */}

      {!isEmpty && !isLoading && (
        <table className={b('wrapper')} style={{ gridTemplateColumns }} ref={ref}>
          <thead className={b('header')}>{renderedHeader}</thead>
          <tbody className={b('body')}>{rows}</tbody>
        </table>
      )}

      {isEmpty && (
        <div className={b('empty')}>
          {isTransactions ? <EmptyTransactionsIcon /> : <img src={EmptyTableIcon} alt="icon" className={b('icon')} />}
          <div className={b('empty-title')}>
            {isTransactions ? tPrefix('table.empty.transactions') : tPrefix('table.empty.title')}
          </div>
          {emptyText && <div className={b('empty-text')}>{emptyText}</div>}
        </div>
      )}

      {pagination && <Pagination pagination={pagination} />}
    </div>
  )
}
